import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageType,
  QueryKeys,
  mutationHandler,
  useAppLocation,
  useAppState,
  useAppTranslation,
} from "../../../common";
import { ErrorModal, Modal } from "../../../components";
import { deletePoetAction } from "../../../actions";
import { PoetEntity } from "../../../entity";

type PoetDeleteModalProps = {
  handleDeleteModalVisible: () => void;
  handleSetPage: (value: number) => void;
};

export function PoetDeleteModal(props: PoetDeleteModalProps) {
  const { t } = useAppTranslation();
  const location = useAppLocation();

  const queryClient = useQueryClient();
  const [state, setState] = useAppState({
    errorModalVisible: false,
  });

  const handleModalVisibility = () => {
    setState((prevState) => ({
      ...prevState,
      errorModalVisible: !prevState.errorModalVisible,
    }));
  };

  const deletePoet = useMutation({
    mutationKey: [QueryKeys.deletePoetQuery],
    mutationFn: deletePoetAction,
    onSuccess() {
      props.handleDeleteModalVisible();
      queryClient.invalidateQueries({ queryKey: [QueryKeys.poetQuery] });
      let page = 1;

      queryClient.setQueryData(
        [QueryKeys.poetQuery],
        (old: PageType<PoetEntity>) => {
          const handler = mutationHandler({
            dataLength: old.data.length,
            pageSize: old.pageSize,
            count: old.count,
          });
          page = handler.page;
          return {
            page: old.page,
            pageSize: old.pageSize,
            count: handler.page,
            data: old.data,
          };
        }
      );
      props.handleSetPage(page);
    },
    onError() {
      handleModalVisibility();
    },
  });

  const handleDeletePoet = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    deletePoet.mutate({ id: location.state?.id });
  };

  return (
    <div>
      <section>
        <Modal
          onClose={props.handleDeleteModalVisible}
          title={t("poet-page-delete-modal-title")}
          footer
          submitText={t("poet-page-delete-submit-button-title")}
          cancelText={t("poet-page-delete-cancel-button-title")}
          handleSubmit={handleDeletePoet}
          loading={deletePoet.isPending}
        >
          <span>{t("poet-page-delete-poet-question-label")}</span>
        </Modal>
      </section>
      <section>
        {state.errorModalVisible && (
          <ErrorModal
            error={deletePoet.error}
            onClose={handleModalVisibility}
            title={t("poet-page-delete-poet-error-modal-title")}
          />
        )}
      </section>
    </div>
  );
}
